if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      const viewerSlider = this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-"]');
      if (viewerSlider) {
        viewerSlider.addEventListener('scroll', this.onViewerScroll.bind(this), { passive: true });
        viewerSlider.addEventListener('scrollend', this.syncActiveMediaFromScroll.bind(this));
      }
      this.bindThumbnailButtons();
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }

    // Slides only. Each slide nests more [data-media-id] of its own (the zoom
    // button, deferred-media, product-model), and counting those as slides lets
    // is-active land on a descendant instead of the slide itself.
    get mediaSlides() {
      return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
        (element) => element.parentElement.closest('[data-media-id]') === null
      );
    }

    getMediaElement(mediaId) {
      return this.mediaSlides.find((element) => element.dataset.mediaId === mediaId);
    }

    bindThumbnailButtons() {
      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (!button || button.dataset.mediaGalleryBound === 'true') return;

        button.dataset.mediaGalleryBound = 'true';
        button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
    }

    onViewerScroll() {
      window.clearTimeout(this.viewerScrollTimer);
      this.viewerScrollTimer = window.setTimeout(this.syncActiveMediaFromScroll.bind(this), 120);
    }

    syncActiveMediaFromScroll() {
      const activeMedia = this.getCurrentMediaElement();
      if (!activeMedia) return;

      // An explicit pick (thumbnail click, variant change) owns the gallery
      // until the track settles on it. Revealing a slide that was display:none
      // reflows the track, and the scroll-snap correction that follows would
      // otherwise hand is-active straight back to the slide we moved away from,
      // which re-hides the variant slide we were trying to show.
      if (this.pendingMediaId && activeMedia.dataset.mediaId !== this.pendingMediaId) return;
      this.clearPendingMedia();

      this.setActiveMediaElement(activeMedia);
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ activeMedia.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.dispatchMediaChange(activeMedia);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.getMediaElement(mediaId);
      if (!activeMedia) return;

      this.pendingMediaId = mediaId;
      window.clearTimeout(this.pendingMediaTimer);
      this.pendingMediaTimer = window.setTimeout(this.clearPendingMedia.bind(this), 1000);

      this.setActiveMediaElement(activeMedia);
      this.setActiveGang(activeMedia);

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail?.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      this.scrollToActiveMedia(activeMedia, mediaId, scrollIntoView);
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.dispatchMediaChange(activeMedia);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    clearPendingMedia() {
      window.clearTimeout(this.pendingMediaTimer);
      this.pendingMediaId = null;
    }

    // Every switch — thumbnail click, arrow, swipe, variant change — moves the
    // gallery the same way. Stating the behavior here rather than leaning on
    // CSS scroll-behavior keeps it identical across breakpoints, where only
    // some of the tracks carry that declaration.
    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    scrollToActiveMedia(activeMedia, mediaId, scrollIntoView) {
      const behavior = this.scrollBehavior;
      let alignedTo = null;

      const align = () => {
        if (this.pendingMediaId !== mediaId) return;

        // Re-issuing a scroll while one is still animating restarts the easing
        // and reads as a stutter, so the correction pass only fires when
        // reflow actually moved the slide.
        const position = `${ activeMedia.offsetLeft }:${ activeMedia.offsetTop }`;
        if (alignedTo === position) return;
        alignedTo = position;

        if (this.elements.thumbnails) {
          const track = activeMedia.parentElement;
          if (Math.round(track.scrollLeft) !== Math.round(activeMedia.offsetLeft)) {
            track.scrollTo({ left: activeMedia.offsetLeft, behavior });
          }
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({ behavior });
        }
      };

      // The first frame lets the browser lay out the slide we just revealed;
      // the delayed pass corrects the scroll-snap adjustment that reflow makes.
      requestAnimationFrame(align);
      window.setTimeout(align, 250);
    }

    getCurrentMediaElement(fallbackElement) {
      const slider = this.elements.viewer.slider;
      const visibleMedia = this.mediaSlides.filter((element) => {
        return element.clientWidth > 0 && element.clientHeight > 0;
      });

      if (!slider || visibleMedia.length === 0) return fallbackElement;

      return visibleMedia.reduce((closestMedia, media) => {
        return Math.abs(media.offsetLeft - slider.scrollLeft) < Math.abs(closestMedia.offsetLeft - slider.scrollLeft)
          ? media
          : closestMedia;
      }, visibleMedia[0]);
    }

    setActiveMediaElement(activeMedia) {
      this.mediaSlides.forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
      this.revealMedia(activeMedia);
    }

    // A variant slide starts out display:none, so its reveal never runs: the
    // IntersectionObserver behind [data-animate-image] cannot see a hidden
    // element, and a lazy image inside one never fires load to drop the
    // loading class. Either one leaves the image at opacity 0 once we do show
    // the slide, so settle both by hand.
    revealMedia(activeMedia) {
      activeMedia.querySelectorAll('use-animate:not([animate])').forEach((element) => {
        element.setAttribute('animate', '');
      });
      activeMedia.querySelectorAll('.media-wrapper.loading').forEach((wrapper) => {
        const image = wrapper.querySelector('img');
        if (!image) return;

        if (image.complete) {
          wrapper.classList.remove('loading');
        } else {
          image.addEventListener('load', () => wrapper.classList.remove('loading'), { once: true });
        }
      });
    }

    setActiveGang(activeMedia) {
      if (!activeMedia.dataset.gangOption || !activeMedia.dataset.gangConnect) return;

      const gangSelector = `[data-gang-option="${ activeMedia.dataset.gangOption }"]`;
      this.querySelectorAll(gangSelector).forEach((element) => {
        element.classList.toggle('gang__active', element.dataset.gangConnect === activeMedia.dataset.gangConnect);
      });
    }

    dispatchMediaChange(activeMedia) {
      document.dispatchEvent(new CustomEvent('product:media:change', {
        detail: {
          mediaId: activeMedia.dataset.mediaId,
          media: activeMedia
        }
      }));
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button')?.removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button')?.setAttribute('aria-current', true);

      const slider = this.elements.thumbnails.slider;
      if (!slider) return;

      const scrollLeft = thumbnail.offsetLeft - (slider.clientWidth - thumbnail.clientWidth) / 2;
      slider.scrollTo({ left: Math.max(scrollLeft, 0), behavior: this.scrollBehavior });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
